import api from './api';

export interface QueueHealth {
  healthy: boolean;
  stats: {
    active: number;
    waiting: number;
    delayed: number;
    failed: number;
    completed: number;
  };
  alerts: string[];
  timestamp: string;
}

export interface FailedJob {
  id: string | number;
  name: string;
  data: any;
  failedReason?: string;
  stacktrace?: string[];
  attemptsMade: number;
  timestamp: number;
}

export interface DelayedJob {
  id: string | number;
  name: string;
  data: any;
  attemptsMade: number;
  delayUntil: string;
  state: string;
}

export interface ActiveJob {
  id: string | number;
  name: string;
  data: any;
  attemptsMade: number;
  progress: number;
}

export interface QueueReport {
  health: QueueHealth;
  failedJobsSample: FailedJob[];
  delayedJobsSample: DelayedJob[];
  activeJobsSample: ActiveJob[];
  generatedAt: string;
}

class WhatsappMonitoringService {
  private baseURL = (
    process.env.REACT_APP_BACKEND_URL || 'http://localhost:3334'
  ).replace(/\/+$/, '');

  /**
   * Obtém status de saúde da fila
   */
  async getQueueHealth(): Promise<{ success: boolean; data: QueueHealth }> {
    try {
      const response = await api.get<{ success: boolean; data: QueueHealth }>(
        `${this.baseURL}/whatsapp/queue/health`,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        `Erro ao obter saúde da fila: ${error instanceof Error ? error.message : 'erro desconhecido'}`,
      );
    }
  }

  /**
   * Obtém relatório completo da fila
   */
  async getQueueReport(): Promise<{ success: boolean; data: QueueReport }> {
    try {
      const response = await api.get<{ success: boolean; data: QueueReport }>(
        `${this.baseURL}/whatsapp/queue/report`,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        `Erro ao obter relatório: ${error instanceof Error ? error.message : 'erro desconhecido'}`,
      );
    }
  }

  /**
   * Obtém jobs falhados
   */
  async getFailedJobs(limit = 50): Promise<{
    success: boolean;
    data: FailedJob[];
    count: number;
  }> {
    try {
      const response = await api.get<{
        success: boolean;
        data: FailedJob[];
        count: number;
      }>(`${this.baseURL}/whatsapp/queue/failed?limit=${limit}`);
      return response.data;
    } catch (error) {
      throw new Error(
        `Erro ao obter jobs falhados: ${error instanceof Error ? error.message : 'erro desconhecido'}`,
      );
    }
  }

  /**
   * Obtém jobs atrasados
   */
  async getDelayedJobs(limit = 50): Promise<{
    success: boolean;
    data: DelayedJob[];
    count: number;
  }> {
    try {
      const response = await api.get<{
        success: boolean;
        data: DelayedJob[];
        count: number;
      }>(`${this.baseURL}/whatsapp/queue/delayed?limit=${limit}`);
      return response.data;
    } catch (error) {
      throw new Error(
        `Erro ao obter jobs atrasados: ${error instanceof Error ? error.message : 'erro desconhecido'}`,
      );
    }
  }

  /**
   * Obtém jobs ativos
   */
  async getActiveJobs(limit = 50): Promise<{
    success: boolean;
    data: ActiveJob[];
    count: number;
  }> {
    try {
      const response = await api.get<{
        success: boolean;
        data: ActiveJob[];
        count: number;
      }>(`${this.baseURL}/whatsapp/queue/active?limit=${limit}`);
      return response.data;
    } catch (error) {
      throw new Error(
        `Erro ao obter jobs ativos: ${error instanceof Error ? error.message : 'erro desconhecido'}`,
      );
    }
  }

  /**
   * Limpa jobs falhados
   */
  async clearFailedJobs(): Promise<{
    success: boolean;
    message: string;
    cleared: number;
  }> {
    try {
      const response = await api.delete<{
        success: boolean;
        message: string;
        cleared: number;
      }>(`${this.baseURL}/whatsapp/queue/failed`);
      return response.data;
    } catch (error) {
      throw new Error(
        `Erro ao limpar jobs falhados: ${error instanceof Error ? error.message : 'erro desconhecido'}`,
      );
    }
  }

  /**
   * Limpa jobs antigos
   */
  async cleanOldJobs(olderThanHours = 24): Promise<{
    success: boolean;
    message: string;
  }> {
    try {
      const response = await api.delete<{
        success: boolean;
        message: string;
      }>(
        `${this.baseURL}/whatsapp/queue/clean?olderThanHours=${olderThanHours}`,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        `Erro ao limpar jobs antigos: ${error instanceof Error ? error.message : 'erro desconhecido'}`,
      );
    }
  }
}

export default new WhatsappMonitoringService();
